import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, RefreshCw, Plus, X, Sparkles, ShieldCheck, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BehaviourDialog({ open, onOpenChange, clone }) {
  const { token, user } = useAuth();
  const [behaviour, setBehaviour] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [newRule, setNewRule] = useState('');
  const [addingRule, setAddingRule] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBehaviour = useCallback(async () => {
    if (!clone?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/persons/${clone.id}/behaviour`, { headers });
      setBehaviour(data.behaviour);
      setPromptDraft(data.behaviour?.system_prompt_override || '');
    } catch {
      toast.error('Erreur lors du chargement du comportement');
    } finally {
      setLoading(false);
    }
  }, [clone?.id, token]);

  useEffect(() => {
    if (open && clone?.id) fetchBehaviour();
  }, [open, clone?.id, fetchBehaviour]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/persons/${clone.id}/behaviour/analyze`, {}, { headers });
      setBehaviour(data.behaviour);
      toast.success(`Analyse terminée : ${data.memories_sampled} mémoires analysées, ${data.patterns_detected} patterns détectés`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  const addRule = async () => {
    if (!newRule.trim()) return;
    setAddingRule(true);
    try {
      await axios.post(`${API}/persons/${clone.id}/behaviour/rules`, { rule: newRule.trim() }, { headers });
      setNewRule('');
      await fetchBehaviour();
      toast.success('Règle ajoutée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setAddingRule(false);
    }
  };

  const toggleRule = async (ruleId, active) => {
    try {
      await axios.patch(`${API}/persons/${clone.id}/behaviour/rules/${ruleId}`, { active }, { headers });
      await fetchBehaviour();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/persons/${clone.id}/behaviour/rules/${ruleId}`, { headers });
      await fetchBehaviour();
      toast.success('Règle supprimée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const savePrompt = async () => {
    try {
      await axios.patch(`${API}/persons/${clone.id}/behaviour/system-prompt`, {
        system_prompt_override: promptDraft.trim() || null,
      }, { headers });
      setEditingPrompt(false);
      await fetchBehaviour();
      toast.success('Prompt sauvegardé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const style = behaviour?.style;
  const rules = behaviour?.rules || [];
  const patterns = behaviour?.patterns || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="behaviour-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Comportement de {clone?.name}
          </DialogTitle>
          <DialogDescription>
            Profil comportemental du clone — style, règles et patterns détectés
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">

            {/* ── Style Section ── */}
            <section data-testid="behaviour-style-section">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Style de communication
              </h3>
              {style?.tone ? (
                <div className="grid grid-cols-2 gap-3">
                  <StyleBadge label="Ton" value={style.tone} />
                  <StyleBadge label="Formalité" value={style.formality} />
                  <StyleBadge label="Longueur" value={style.response_length} />
                  <StyleBadge label="Langue" value={style.language} />
                  {style.signature_phrases?.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-xs text-muted-foreground">Expressions typiques</span>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {style.signature_phrases.map((p, i) => (
                          <Badge key={i} variant="secondary" className="text-xs font-normal">&laquo; {p} &raquo;</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Aucun style détecté. Lancez une analyse pour détecter automatiquement.
                </p>
              )}
            </section>

            <Separator />

            {/* ── Rules Section ── */}
            <section data-testid="behaviour-rules-section">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Règles de conduite
              </h3>
              <div className="space-y-2">
                {rules.map((r) => (
                  <div key={r.id} data-testid={`rule-${r.id}`}
                    className={`flex items-center justify-between gap-2 p-2.5 rounded-lg border text-sm ${r.active ? 'bg-card' : 'bg-muted/50 opacity-60'}`}>
                    <div className="flex-1">
                      <span className={r.active ? '' : 'line-through'}>{r.rule}</span>
                      <Badge variant="outline" className="ml-2 text-[10px]">{r.source}</Badge>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => toggleRule(r.id, !r.active)}
                            data-testid={`rule-toggle-${r.id}`}>
                            {r.active ? <X className="w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => deleteRule(r.id)}
                            data-testid={`rule-delete-${r.id}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {rules.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Aucune règle définie.</p>
                )}
              </div>

              {/* Add rule */}
              <div className="flex gap-2 mt-3">
                <Input
                  placeholder="Nouvelle règle..."
                  value={newRule}
                  onChange={(e) => setNewRule(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addRule()}
                  data-testid="new-rule-input"
                  className="text-sm"
                />
                <Button size="sm" onClick={addRule} disabled={addingRule || !newRule.trim()} data-testid="add-rule-btn">
                  {addingRule ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
            </section>

            <Separator />

            {/* ── Patterns Section ── */}
            <section data-testid="behaviour-patterns-section">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Patterns détectés
                {patterns.length > 0 && (
                  <Badge variant="outline" className="text-[10px]">{patterns.length}</Badge>
                )}
              </h3>
              {patterns.length > 0 ? (
                <div className="space-y-2">
                  {patterns.map((p) => (
                    <div key={p.id} className="p-2.5 rounded-lg border bg-card text-sm" data-testid={`pattern-${p.id}`}>
                      <div className="flex items-center justify-between">
                        <span>{p.pattern}</span>
                        <Badge variant={p.confidence > 0.7 ? 'default' : 'secondary'} className="text-[10px] ml-2 shrink-0">
                          {Math.round(p.confidence * 100)}%
                        </Badge>
                      </div>
                      {p.examples && (
                        <p className="text-xs text-muted-foreground mt-1 italic">&laquo; {p.examples} &raquo;</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  Aucun pattern détecté. Lancez une analyse pour détecter automatiquement.
                </p>
              )}
            </section>

            {/* ── System Prompt Override (admin only) ── */}
            {isAdmin && (
              <>
                <Separator />
                <section data-testid="behaviour-prompt-section">
                  <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Pencil className="w-4 h-4" /> Prompt personnalisé
                  </h3>
                  {editingPrompt ? (
                    <div className="space-y-2">
                      <Textarea
                        value={promptDraft}
                        onChange={(e) => setPromptDraft(e.target.value)}
                        placeholder="Instructions supplémentaires pour le clone..."
                        rows={3}
                        className="text-sm"
                        data-testid="prompt-textarea"
                      />
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setEditingPrompt(false)}>Annuler</Button>
                        <Button size="sm" onClick={savePrompt} data-testid="save-prompt-btn">Sauvegarder</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-muted-foreground italic flex-1">
                        {behaviour?.system_prompt_override || 'Aucun prompt personnalisé.'}
                      </p>
                      <Button variant="ghost" size="sm" onClick={() => setEditingPrompt(true)} data-testid="edit-prompt-btn">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                      </Button>
                    </div>
                  )}
                </section>
              </>
            )}

            {/* ── Analysis Metadata & Action ── */}
            <Separator />
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {behaviour?.analysis_date ? (
                  <>
                    Dernière analyse : {new Date(behaviour.analysis_date).toLocaleDateString('fr-FR')}
                    {' '}&middot; {behaviour.memories_sampled} mémoires
                    {' '}&middot; v{behaviour.analysis_version}
                  </>
                ) : (
                  'Aucune analyse effectuée'
                )}
              </div>
              {isAdmin && (
                <Button onClick={runAnalysis} disabled={analyzing} size="sm" variant="outline" data-testid="analyze-btn">
                  {analyzing ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Analyse en cours...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-1.5" /> {behaviour?.analysis_date ? 'Ré-analyser' : 'Lancer l\'analyse'}</>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StyleBadge({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="mt-0.5">
        <Badge variant="outline" className="font-normal capitalize">{value}</Badge>
      </div>
    </div>
  );
}
